/**
 * AiSettings — memory and suggestion preferences for the followed AI buddy.
 *
 * Deviation from mobile: the buddy comes from the profile's followed agent
 * instead of the first entry of the roster, so members without a buddy get an
 * explicit call to action rather than settings for an agent they never chose.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { ToastService } from '@wlb/shared/platform/notifier';
import { useAiBuddySettings } from '@wlb/shared/hooks/useAiBuddySettings';
import { Button } from '../components/ui/Button';
import { ListStatus } from '../components/ui/ListStatus';
import { Switch } from '../components/ui/Switch';
import { CheckCircleIcon, CommentIcon, HeartIcon, RobiIcon, TrashIcon } from '../components/ui/icons';
import { MorePage } from '../features/more/MorePage';
import { MoreLoading } from '../features/more/MoreStatus';
import { AiBuddyDisclaimerModal } from '../features/settings/AiBuddyDisclaimerModal';
import '../features/settings/settings.css';

export function AiSettings() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    settings,
    buddyName,
    hasAiBuddy,
    loading,
    saving,
    error,
    hasChanges,
    setField,
    reload,
    save,
  } = useAiBuddySettings();
  const [disclaimerOpen, setDisclaimerOpen] = useState(false);

  const name = buddyName || t('aiBuddy.settings.defaultName');
  const title = t('aiBuddy.settings.title', { name });

  const handleSave = async () => {
    const result = await save();
    if (result.success) {
      ToastService.show({ type: 'success', message: t('aiBuddy.settings.saveSuccess') });
      return;
    }
    ToastService.show({
      type: 'error',
      message: result.message || t('common.errors.generic'),
    });
  };

  const renderBody = () => {
    if (!hasAiBuddy) {
      return (
        <>
          <ListStatus
            variant="empty"
            title={t('aiBuddy.settings.noBuddyTitle')}
            message={t('aiBuddy.settings.noBuddyDescription')}
          />
          <div className="settings-actions">
            <Button onClick={() => navigate('/choose-ai-buddy')}>
              {t('aiBuddy.settings.noBuddyCta')}
            </Button>
          </div>
        </>
      );
    }
    if (loading) return <MoreLoading variant="card" />;
    if (error) return <ListStatus variant="error" message={error} onAction={reload} />;

    return (
      <>
        <section className="settings-section" aria-labelledby="ai-memory-title">
          <h2 className="settings-section__title" id="ai-memory-title">
            {t('aiBuddy.settings.memory')}
          </h2>
          <ul className="settings-options">
            {([
              ['keep', true, RobiIcon] as const,
              ['erase', false, TrashIcon] as const,
            ]).map(([key, keepHistory, Icon]) => {
              const selected = settings.keepHistoryFor30Days === keepHistory;
              return (
                <li key={key}>
                  <button
                    type="button"
                    className={`settings-option${selected ? ' settings-option--selected' : ''}`}
                    aria-pressed={selected}
                    onClick={() => setField('keepHistoryFor30Days', keepHistory)}
                  >
                    <span className="settings-option__icon">
                      <Icon size={18} />
                    </span>
                    <span className="settings-option__copy">
                      <span className="settings-option__title">
                        {t(keepHistory
                          ? 'aiBuddy.settings.keepHistory'
                          : 'aiBuddy.settings.eraseHistory')}
                      </span>
                      <span className="settings-option__description">
                        {keepHistory
                          ? t('aiBuddy.settings.keepHistoryDesc', { name })
                          : t('aiBuddy.settings.eraseHistoryDesc')}
                      </span>
                    </span>
                    {selected && (
                      <CheckCircleIcon size={20} className="settings-option__check" />
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </section>

        <section className="settings-section" aria-labelledby="ai-action-title">
          <h2 className="settings-section__title" id="ai-action-title">
            {t('aiBuddy.settings.action')}
          </h2>
          <div className="settings-card">
            <div className="settings-row">
              <span className="settings-row__icon">
                <HeartIcon size={18} />
              </span>
              <span className="settings-row__copy">
                <span className="settings-row__title" id="ai-suggest-likes">
                  {t('aiBuddy.settings.suggestLikes')}
                </span>
                <span className="settings-row__description">
                  {t('aiBuddy.settings.suggestLikesDesc', { name })}
                </span>
              </span>
              <Switch
                checked={settings.enableSuggestLikes}
                onChange={value => setField('enableSuggestLikes', value)}
                label={t('aiBuddy.settings.suggestLikes')}
                labelledBy="ai-suggest-likes"
              />
            </div>
            <div className="settings-row">
              <span className="settings-row__icon">
                <CommentIcon size={18} />
              </span>
              <span className="settings-row__copy">
                <span className="settings-row__title" id="ai-suggest-comments">
                  {t('aiBuddy.settings.suggestComments')}
                </span>
                <span className="settings-row__description">
                  {t('aiBuddy.settings.suggestCommentsDesc', { name })}
                </span>
              </span>
              <Switch
                checked={settings.enableSuggestComments}
                onChange={value => setField('enableSuggestComments', value)}
                label={t('aiBuddy.settings.suggestComments')}
                labelledBy="ai-suggest-comments"
              />
            </div>
          </div>
        </section>

        <div className="settings-actions">
          <Button
            fullWidth
            disabled={saving || !hasChanges}
            onClick={() => { handleSave().catch(() => undefined); }}
          >
            {t(saving ? 'common.buttons.saving' : 'aiBuddy.settings.saveChanges')}
          </Button>
          <Button variant="outline" fullWidth disabled={saving} onClick={() => navigate(-1)}>
            {t('aiBuddy.settings.cancel')}
          </Button>
          <Button variant="ghost" fullWidth onClick={() => setDisclaimerOpen(true)}>
            {t('aiBuddy.settings.viewDisclaimer')}
          </Button>
        </div>
      </>
    );
  };

  return (
    <MorePage title={title} subtitle={t('aiBuddy.settings.subtitle', { name })}>
      {renderBody()}
      {disclaimerOpen && (
        <AiBuddyDisclaimerModal onClose={() => setDisclaimerOpen(false)} />
      )}
    </MorePage>
  );
}
